import { Reservation } from 'types/reservations';
import { TicketDocument } from 'types/shared/documents';
import { ReservationShift } from 'types/shifts';

type BookedTicket = Omit<TicketDocument, 'reservation'> & {
  reservation: Reservation | null;
};

// Cancelling a reservation leaves its paid ticket on succedded. A no-show is
// stored as failed too, but keeps its ticket.
export const hasCancelledReservation = (ticket: BookedTicket) =>
  ticket.reservation?.status === 'failed' && !ticket.reservation.noShow;

export const isSoldTicket = (ticket: BookedTicket) =>
  (ticket.status === 'succedded' || ticket.status === 'processing') &&
  !!ticket.reservation &&
  !hasCancelledReservation(ticket);

// A rebooked ticket keeps the event id of its old date. The event shift that
// takes its reservation then tells the event.
export const getEventIdOfTicket = (
  ticket: BookedTicket,
  shifts: ReservationShift[],
) => {
  const { reservation } = ticket;

  if (!reservation) return ticket.event.id;

  const taking = shifts.filter(
    (s) =>
      s.active &&
      !s.disabled &&
      !s.closed &&
      s.additional?.eventV02 &&
      s.occassions.includes(reservation.occassion),
  );
  const start = reservation.startTimeInBlocks;
  // Consecutive shifts share their edge block. A ticket of one of the two
  // events stays with its own there, any other goes to the one starting there.
  const running = taking
    .filter((s) => s.start <= start && s.close >= start)
    .sort((a, b) => b.start - a.start || a.close - b.close);
  const eventIds = (running.length ? running : taking).map(
    (s) => s.additional?.eventV02?.id,
  );

  if (eventIds.includes(ticket.event.id)) return ticket.event.id;

  return running.length || new Set(eventIds).size === 1
    ? eventIds[0]
    : ticket.event.id;
};
